import { useEffect, useMemo, useState, useCallback } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import ImageUpload from "@/components/admin/ImageUpload";
import { supabase } from "@/lib/supabase";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MagnifyingGlass,
  Plus,
  Pencil,
  Trash,
  Eye,
  ArrowClockwise,
  CheckCircle,
  Prohibit,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { detectColumns, safeInsert, safeUpdate } from "@/lib/schema";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

const STATUS = [
  { value: "draft", label: "Draft", tone: "bg-slate-100 text-slate-700" },
  { value: "published", label: "Published", tone: "bg-emerald-50 text-emerald-700 border border-emerald-200" },
];
const statusTone = (s) => STATUS.find((x) => x.value === s)?.tone || "bg-slate-100 text-slate-700";
const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
const readingTime = (html) => {
  const text = (html || "").replace(/<[^>]+>/g, " ");
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
};

const EMPTY = {
  id: null,
  title: "",
  slug: "",
  short_description: "",
  category: "",
  content: "",
  seo_title: "",
  seo_description: "",
  keywords: "",
  author: "Aeonyx Editorial",
  reading_minutes: 3,
  read_minutes: 3,
  is_featured: false,
  cover_image: "",
  status: "draft",
  is_published: false,
  published_at: null,
};

export default function AdminBlogs() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("blogs")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(500);
    if (error) toast.error(error.message);
    setRows(data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const channel = supabase
      .channel("admin-blogs")
      .on("postgres_changes", { event: "*", schema: "public", table: "blogs" }, () => load())
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [load]);

  const categories = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => r.category && set.add(r.category));
    return Array.from(set);
  }, [rows]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return rows.filter((b) => {
      const bStatus = b.status || (b.is_published ? "published" : "draft");
      if (statusFilter !== "all" && bStatus !== statusFilter) return false;
      if (categoryFilter !== "all" && (b.category || "") !== categoryFilter) return false;
      if (!query) return true;
      return `${b.title || ""} ${b.slug || ""} ${b.author || ""}`.toLowerCase().includes(query);
    });
  }, [rows, q, statusFilter, categoryFilter]);

  const openNew = () => setEditing({ ...EMPTY });
  const openEdit = (b) => setEditing({ ...EMPTY, ...b });

  const persist = async (payload, publish = null) => {
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const nextStatus =
        publish === true ? "published" : publish === false ? "draft" : payload.status;
      const full = {
        title: payload.title,
        slug: payload.slug || slugify(payload.title),
        short_description: payload.short_description,
        excerpt: payload.short_description,
        category: payload.category,
        content: payload.content,
        seo_title: payload.seo_title,
        seo_description: payload.seo_description,
        keywords: payload.keywords,
        author: payload.author,
        reading_minutes: Number(payload.reading_minutes) || readingTime(payload.content),
        read_minutes: Number(payload.read_minutes) || readingTime(payload.content),
        is_featured: !!payload.is_featured,
        cover_image: payload.cover_image,
        image: payload.cover_image,
        thumbnail: payload.cover_image,
        status: nextStatus,
        is_published: nextStatus === "published",
        published_at:
          nextStatus === "published" ? payload.published_at || now : payload.published_at || null,
      };
      const res = payload.id
        ? await safeUpdate("blogs", full, { id: payload.id })
        : await safeInsert("blogs", full);
      if (res.error) throw res.error;
      toast.success(payload.id ? "Blog updated" : "Blog created");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete "${b.title}"? This cannot be undone.`)) return;
    const { error } = await supabase.from("blogs").delete().eq("id", b.id);
    if (error) toast.error(error.message);
    else {
      toast.success("Blog deleted");
      load();
    }
  };

  const togglePublish = async (b) => {
    const nowPublished = !(b.status === "published" || b.is_published);
    const cols = await detectColumns("blogs");
    const patch = {};
    if (cols.has("status") || cols.size === 0) patch.status = nowPublished ? "published" : "draft";
    if (cols.has("is_published") || cols.size === 0) patch.is_published = nowPublished;
    if (cols.has("published_at") || cols.size === 0)
      patch.published_at = nowPublished ? new Date().toISOString() : null;
    if (Object.keys(patch).length === 0) {
      toast.error("Your blogs table has no publish/status column.");
      return;
    }
    const { error } = await supabase.from("blogs").update(patch).eq("id", b.id);
    if (error) toast.error(error.message);
    else {
      toast.success(nowPublished ? "Blog published" : "Blog unpublished");
      load();
    }
  };

  return (
    <AdminLayout title="Blogs">
      {/* Toolbar */}
      <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between mb-5">
        <div className="flex-1 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 max-w-sm min-w-[200px]">
            <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="blogs-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by title, slug…"
              className="pl-9 h-10 rounded-xl bg-white"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger data-testid="blogs-category-filter" className="w-44 h-10 rounded-xl bg-white">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="blogs-status-filter" className="w-40 h-10 rounded-xl bg-white">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUS.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} data-testid="blogs-refresh" className="rounded-xl h-10 border-slate-200">
            <ArrowClockwise size={14} weight="bold" className="mr-1.5" />
            Refresh
          </Button>
          <Button onClick={openNew} data-testid="blogs-add" className="rounded-xl h-10 bg-navy-deep hover:bg-navy text-white">
            <Plus size={14} weight="bold" className="mr-1.5" />
            Add Blog
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="h-14 rounded-xl bg-slate-100 animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-500">
            No blogs match your filters. Click <b>Add Blog</b> to create one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-widest text-slate-500 font-mono-data">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">Cover</th>
                  <th className="text-left px-5 py-3 font-semibold">Title</th>
                  <th className="text-left px-5 py-3 font-semibold">Slug</th>
                  <th className="text-left px-5 py-3 font-semibold">Category</th>
                  <th className="text-left px-5 py-3 font-semibold">Author</th>
                  <th className="text-left px-5 py-3 font-semibold">Status</th>
                  <th className="text-left px-5 py-3 font-semibold">Published</th>
                  <th className="text-left px-5 py-3 font-semibold">Created</th>
                  <th className="text-right px-5 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((b) => {
                  const status = b.status || (b.is_published ? "published" : "draft");
                  const cover = b.cover_image || b.image || b.thumbnail;
                  return (
                    <tr key={b.id} className="border-t border-slate-100 hover:bg-slate-50/60 transition-colors">
                      <td className="px-5 py-3">
                        {cover ? (
                          <img src={cover} alt="" className="h-10 w-16 rounded-lg object-cover border border-slate-200" />
                        ) : (
                          <div className="h-10 w-16 rounded-lg bg-slate-100 border border-slate-200" />
                        )}
                      </td>
                      <td className="px-5 py-3 text-navy-deep font-semibold max-w-xs truncate">{b.title}</td>
                      <td className="px-5 py-3 text-slate-500 font-mono-data text-[12px]">/{b.slug || "—"}</td>
                      <td className="px-5 py-3 text-slate-600">{b.category || "—"}</td>
                      <td className="px-5 py-3 text-slate-600">{b.author || "—"}</td>
                      <td className="px-5 py-3">
                        <span className={`text-[10.5px] uppercase tracking-[0.14em] font-mono-data font-semibold px-2 py-1 rounded-full ${statusTone(status)}`}>
                          {status}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-slate-500 text-[12px]">
                        {b.published_at ? new Date(b.published_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-5 py-3 text-slate-500 text-[12px]">
                        {b.created_at ? new Date(b.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex justify-end gap-1">
                          {b.slug && (
                            <a
                              href={`/#blogs`}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center h-8 w-8 rounded-lg text-slate-500 hover:text-cyan-brand hover:bg-slate-100 justify-center"
                              title="Preview"
                              data-testid={`blog-preview-${b.id}`}
                            >
                              <Eye size={14} weight="regular" />
                            </a>
                          )}
                          <button
                            onClick={() => togglePublish(b)}
                            className={`inline-flex items-center h-8 w-8 rounded-lg justify-center ${
                              status === "published"
                                ? "text-emerald-600 hover:bg-emerald-50"
                                : "text-slate-500 hover:text-emerald-600 hover:bg-slate-100"
                            }`}
                            title={status === "published" ? "Unpublish" : "Publish"}
                            data-testid={`blog-toggle-${b.id}`}
                          >
                            {status === "published" ? (
                              <Prohibit size={14} weight="regular" />
                            ) : (
                              <CheckCircle size={14} weight="regular" />
                            )}
                          </button>
                          <button
                            onClick={() => openEdit(b)}
                            className="inline-flex items-center h-8 w-8 rounded-lg text-slate-500 hover:text-cyan-brand hover:bg-slate-100 justify-center"
                            title="Edit"
                            data-testid={`blog-edit-${b.id}`}
                          >
                            <Pencil size={14} weight="regular" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <BlogEditor
          value={editing}
          onChange={setEditing}
          onSaveDraft={() => persist(editing, false)}
          onPublish={() => persist(editing, true)}
          onCancel={() => setEditing(null)}
          saving={saving}
        />
      )}
    </AdminLayout>
  );
}

function BlogEditor({ value, onChange, onSaveDraft, onPublish, onCancel, saving }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const isEdit = !!value.id;

  const submit = (e, publish) => {
    e.preventDefault();
    if (!value.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!(value.content || "").replace(/<[^>]+>/g, "").trim()) {
      toast.error("Content is required");
      return;
    }
    publish ? onPublish() : onSaveDraft();
  };

  return (
    <Dialog open onOpenChange={(o) => (o ? null : onCancel())}>
      <DialogContent
        data-testid="blog-editor"
        className="max-w-4xl max-h-[92vh] overflow-y-auto p-0"
      >
        <DialogHeader className="p-6 border-b border-slate-200">
          <DialogTitle className="font-display text-2xl text-navy-deep">
            {isEdit ? "Edit blog post" : "New blog post"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={(e) => submit(e, false)} className="p-6 space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.12em] text-navy-deep font-semibold">
                Blog title <span className="text-red-500">*</span>
              </Label>
              <Input
                data-testid="blog-title"
                value={value.title}
                onChange={(e) => {
                  const title = e.target.value;
                  set({ title, slug: value.slug ? value.slug : slugify(title) });
                }}
                required
                className="mt-1.5 h-11 rounded-xl"
                placeholder="Peptide storage 101"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.12em] text-navy-deep font-semibold">Slug</Label>
              <Input
                data-testid="blog-slug"
                value={value.slug}
                onChange={(e) => set({ slug: slugify(e.target.value) })}
                className="mt-1.5 h-11 rounded-xl font-mono-data text-[13px]"
                placeholder="peptide-storage-101"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.12em] text-navy-deep font-semibold">Category</Label>
              <Input
                data-testid="blog-category"
                value={value.category || ""}
                onChange={(e) => set({ category: e.target.value })}
                className="mt-1.5 h-11 rounded-xl"
                placeholder="Cold Chain"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.12em] text-navy-deep font-semibold">Author</Label>
              <Input
                data-testid="blog-author"
                value={value.author || ""}
                onChange={(e) => set({ author: e.target.value })}
                className="mt-1.5 h-11 rounded-xl"
              />
            </div>
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-[0.12em] text-navy-deep font-semibold">Short description</Label>
            <Textarea
              data-testid="blog-short"
              value={value.short_description || ""}
              onChange={(e) => set({ short_description: e.target.value })}
              rows={2}
              className="mt-1.5 rounded-xl"
              placeholder="One-sentence hook (appears in the blog card)."
            />
          </div>

          <ImageUpload
            value={value.cover_image}
            onChange={(url) => set({ cover_image: url })}
            folder="blogs"
            testId="blog-cover"
          />

          <div>
            <Label className="text-[11px] uppercase tracking-[0.12em] text-navy-deep font-semibold">
              Content <span className="text-red-500">*</span>
            </Label>
            <div className="mt-1.5 rounded-xl border border-slate-200 overflow-hidden bg-white">
              <ReactQuill
                data-testid="blog-content"
                theme="snow"
                value={value.content || ""}
                onChange={(html) => set({ content: html })}
                modules={{
                  toolbar: [
                    [{ header: [1, 2, 3, false] }],
                    ["bold", "italic", "underline", "strike"],
                    [{ list: "ordered" }, { list: "bullet" }],
                    ["blockquote", "code-block"],
                    ["link", "image"],
                    ["clean"],
                  ],
                }}
                style={{ minHeight: 220 }}
              />
            </div>
          </div>

          <div className="rounded-2xl bg-slate-50/60 border border-slate-200 p-4">
            <div className="font-display font-semibold text-navy-deep mb-3">SEO</div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-[11px] uppercase tracking-[0.12em] text-navy-deep font-semibold">SEO title</Label>
                <Input
                  data-testid="blog-seo-title"
                  value={value.seo_title || ""}
                  onChange={(e) => set({ seo_title: e.target.value })}
                  className="mt-1.5 h-11 rounded-xl"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.12em] text-navy-deep font-semibold">Keywords</Label>
                <Input
                  data-testid="blog-keywords"
                  value={value.keywords || ""}
                  onChange={(e) => set({ keywords: e.target.value })}
                  className="mt-1.5 h-11 rounded-xl"
                  placeholder="peptide storage, cold chain, potency"
                />
              </div>
            </div>
            <div className="mt-4">
              <Label className="text-[11px] uppercase tracking-[0.12em] text-navy-deep font-semibold">SEO description</Label>
              <Textarea
                data-testid="blog-seo-desc"
                value={value.seo_description || ""}
                onChange={(e) => set({ seo_description: e.target.value })}
                rows={2}
                className="mt-1.5 rounded-xl"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.12em] text-navy-deep font-semibold">Reading time (min)</Label>
              <Input
                data-testid="blog-reading"
                type="number"
                min="1"
                value={value.reading_minutes}
                onChange={(e) =>
                  set({ reading_minutes: Number(e.target.value), read_minutes: Number(e.target.value) })
                }
                className="mt-1.5 h-11 rounded-xl"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.12em] text-navy-deep font-semibold">Publish date</Label>
              <Input
                data-testid="blog-publish-date"
                type="datetime-local"
                value={
                  value.published_at
                    ? new Date(value.published_at).toISOString().slice(0, 16)
                    : ""
                }
                onChange={(e) =>
                  set({ published_at: e.target.value ? new Date(e.target.value).toISOString() : null })
                }
                className="mt-1.5 h-11 rounded-xl"
              />
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-white border border-slate-200 cursor-pointer w-full">
                <input
                  type="checkbox"
                  data-testid="blog-featured"
                  checked={!!value.is_featured}
                  onChange={(e) => set({ is_featured: e.target.checked })}
                  className="h-4 w-4"
                />
                <span className="text-[13px] font-semibold text-navy-deep">Featured blog</span>
              </label>
            </div>
          </div>

          <div className="pt-4 border-t border-slate-200 flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              data-testid="blog-cancel"
              className="rounded-full h-11 px-5 border-slate-200"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid="blog-save-draft"
              className="rounded-full h-11 px-5 bg-white text-navy-deep border border-navy-deep hover:bg-slate-50"
            >
              {saving ? "Saving…" : "Save draft"}
            </Button>
            <Button
              type="button"
              disabled={saving}
              onClick={(e) => submit(e, true)}
              data-testid="blog-publish"
              className="rounded-full h-11 px-5 bg-cta-gradient text-white font-semibold"
            >
              {isEdit && (value.status === "published" || value.is_published) ? "Update" : "Publish"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
